<template>
  <v-flex
    id="jobless"
    class="question"
  >
    <v-flex v-if="getJoblessQuestions">
      <p class="mb-0 text-uppercase">
        {{ $t('sheet.sheetOne.meeting.jobless.subtitle') }}
      </p>
      <h2 class="display-3 display-3_dark">
        {{ getJoblessQuestions.label }}
      </h2>
      <ol class="questions-list">
        <li
          v-if="getJoblessQuestions.hasOwnProperty('is_ready_for_work')"
          class="mt-4"
        >
          <p
            class="header"
            :class="{ required: getJoblessQuestions.is_ready_for_work.required }"
          >
            {{ getJoblessQuestions.is_ready_for_work.label }}
          </p>
          <ConfirmButtons
            v-model="isReadyForWork"
            v-validate="{ required: getJoblessQuestions.is_ready_for_work.required }"
            string
            :disabled="readOnly"
            :error-messages="errors.collect('isReadyForWork')"
            :data-vv-as="$t('label.question')"
            :data-vv-name="'isReadyForWork'"
          />
        </li>
        <li
          v-if="isReadyForWork === 'yes' && getJoblessQuestions.hasOwnProperty('selected_members')"
          class="mt-4"
        >
          <p
            class="header custom lA"
            :class="{ required: getJoblessQuestions.selected_members.required }"
          >
            {{ getJoblessQuestions.selected_members.label }}
          </p>
          <v-checkbox
            v-for="(item, index) in representatives"
            :key="index"
            v-model="selectedMembers"
            v-validate="{ required: getJoblessQuestions.selected_members.required }"
            :disabled="readOnly"
            :label="item.firstName"
            :value="item.id"
            type="checkbox"
            multiple
            :error="errors.has('selectedMembers')"
            :error-messages="
              index + 1 === representatives.length ? errors.collect('selectedMembers') : null
            "
            :data-vv-as="$t('label.question')"
            :data-vv-name="'selectedMembers'"
          />
        </li>
        <li
          v-if="getJoblessQuestions.hasOwnProperty('about_sixty') && !hideAboutSixty"
          class="mt-4"
        >
          <p
            class="header"
            :class="{ required: getJoblessQuestions.about_sixty.required }"
          >
            {{ getJoblessQuestions.about_sixty.label }}
          </p>
          <ConfirmButtons
            v-model="aboutSixty"
            v-validate="{ required: getJoblessQuestions.about_sixty.required }"
            string
            :error-messages="errors.collect('aboutSixty')"
            :data-vv-as="$t('label.question')"
            :disabled="readOnly"
            :data-vv-name="'aboutSixty'"
          />
          <p
            v-if="aboutSixty === 'yes'"
            class="f-14 mt-3"
          >
            {{ $t('sheet.sheetOne.meeting.jobless.senior_hint') }}
          </p>
        </li>
      </ol>
    </v-flex>
    <div v-if="getJoblessQuestions.hasOwnProperty('members')">
      <template v-for="(item, index) in representatives">
        <div
          v-if="selectedMembers.includes(item.id) && isReadyForWork === 'yes'"
          :key="index"
        >
          <v-divider class="mt-5" />
          <!-- eslint-disable-next-line vue/valid-v-model -->
          <memberForm
            v-model="members[findMemberIndex(item.id)]"
            :read-only="readOnly"
            :is-current-edition="isCurrentEdition"
            class="pt-3 mt-4"
          />
        </div>
      </template>
    </div>
    <v-layout
      v-if="endButton != null"
      justify-center
      my-3
    >
      <v-flex shrink>
        <div>
          <v-btn
            color="sz_violet"
            :to="{ name: endButton.to }"
          >
            {{ endButton.text }}
          </v-btn>
        </div>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import memberForm from '@/components/widgets/sheet/questions/jobless/memberForm';
import validationRules from '@/constants/validationRules';
import sheetSectionsVisibility from '@/mixins/sheet/sheetSectionsVisibility';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';

import { mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';

import debounce from 'lodash.debounce';
import uniqBy from 'lodash.uniqby';

const { mapFields } = createHelpers({
    getterType: 'sheet/getJoblessField',
    mutationType: 'sheet/updateJoblessField'
});

export default {
    name: 'QuestionsJobless',
    components: {
        ConfirmButtons,
        memberForm
    },
    mixins: [sheetValidationRules, sheetSectionsVisibility],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        },
        endButton: {
            default: null
        },
        hideAboutSixty: {
            type: Boolean,
            default: false
        },
        isCurrentEdition: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ...mapFields(['isReadyForWork', 'selectedMembers', 'members', 'aboutSixty']),
        ...mapGetters('sheet', ['getJoblessQuestions']),
        representatives () {
            return this.$store.getters['sheet/getRepresentatives'];
        }
    },
    watch: {
        representatives: {
            handler (val) {
                this.debouncedCreateMember(val);
            },
            deep: true
        },
        selectedMembers (valN, valO) {
            if (valN.length < valO.length) {
                this.members.forEach((el) => {
                    if (!valN.includes(el.id)) {
                        for (const prop in el) {
                            if (Object.prototype.hasOwnProperty.call(el, prop)) {
                                if (prop === 'name' || prop === 'id') {
                                    continue;
                                }
                                if (Array.isArray(el[prop])) {
                                    el[prop] = [];
                                } else {
                                    el[prop] = null;
                                }
                            }
                        }
                    }
                });
            }
        },
        isReadyForWork (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.selectedMembers = [];
                });
            }
        }
    },
    data () {
        return {
            validationRules,
            emptyMembersData: {
                exp: null,
                howLong: null,
                cause: null,
                whatAtWork: null,
                selectedExpectations: [],
                otherExpectation: null,
                lookingForWork: null,
                selectedAnnouncements: [],
                otherAnnouncement: null,
                participates: null,
                participatesExplain: null,
                cvCount: null,
                interviewsCount: null,
                interviewsExplain: null,
                refusedJob: null,
                selectedJobRefuse: [],
                jobRefuseOther: null,
                obstacles: null,
                obstaclesDescription: null,
                jobFor: null,
                notLookingForWorkExplain: null,
                planToFindAJob: null
            }
        };
    },
    created () {
        this.createMember(this.representatives);
    },
    methods: {
        createMember (val) {
            if (val != null && val.length > 0) {
                val.forEach((e) => {
                    if (
                        this.members.find((el) => {
                            return el.id === e.id;
                        })
                    ) {
                        const index = this.members.findIndex((el) => {
                            return el.id === e.id;
                        });
                        const member = this.members[index];
                        this.members[index] = {
                            ...member,
                            id: e.id,
                            name: e.firstName
                        };
                    } else {
                        this.members.push({
                            id: e.id,
                            name: e.firstName,
                            ...this.emptyMembersData
                        });
                    }
                });
                const _id = Array.from(val, (x) => x.id);
                this.members = this.members.filter((member) => _id.includes(member.id));
                this.members = uniqBy(this.members, 'id');
                this.selectedMembers = this.selectedMembers.filter((selected) => _id.includes(selected));
            }
        },
        debouncedCreateMember: debounce(function (value) {
            this.createMember(value);
        }, 1000),
        findMemberIndex (id) {
            const member = this.members.find((el) => {
                return el.id === id;
            });
            return this.members.indexOf(member);
        }
    },
    beforeDestroy () {
        this.members = [];
    }
};
</script>

<style scoped></style>
